import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MapPin, CalendarDays, ExternalLink, Lock } from "lucide-react";
import { fmtDateTime, money, compact } from "@/lib/api";

export const METRIC_DEFS = [
  ["partnership_spend", "Partnership spend", (v) => money(v)],
  ["deals_won", "Deals won", (v) => v],
  ["deals_in_progress", "Deals in progress", (v) => v],
  ["roi", "ROI", (v) => `${v}%`],
  ["leads_generated", "Leads generated", (v) => compact(v)],
  ["audience_reach", "Audience reach", (v) => compact(v)],
  ["impressions", "Impressions", (v) => compact(v)],
  ["engagement", "Engagement", (v) => `${v}%`],
  ["revenue_generated", "Revenue generated", (v) => money(v)],
  ["cost_per_acquisition", "Cost per acquisition", (v) => money(v, 2)],
];

export const MetricGrid = ({ m }) => (
  <div className="grid grid-cols-2 sm:grid-cols-5 gap-px rounded-xl overflow-hidden bg-[#2A1240]/10" data-testid="event-metrics-grid">
    {METRIC_DEFS.map(([k, label, f]) => (
      <div key={k} className="bg-[#FFFFFF] p-4">
        <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400">{label}</p>
        <p className={`mt-1 font-mono text-lg ${k === "roi" ? "text-emerald-300" : "text-[#2A1240]"}`} data-testid={`metric-${k}`}>{f(m?.[k] ?? 0)}</p>
      </div>
    ))}
  </div>
);

export default function EventDialog({ event, onClose }) {
  const e = event;
  return (
    <Dialog open={!!e} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden bg-[#FFF7FB] border-[#2A1240]/10 max-h-[90vh] overflow-y-auto" data-lenis-prevent data-testid="event-dialog">
        {e && (
          <>
            {e.image_url && (
              <div className="relative aspect-[21/9]">
                <img src={e.image_url} alt={e.title} className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FFF7FB] to-transparent" />
              </div>
            )}
            <div className="p-6 sm:p-8 -mt-10 relative">
              <p className="kicker">{e.type} · {e.company_name}</p>
              <DialogTitle className="mt-3 font-display text-3xl text-[#2A1240] font-normal">{e.title}</DialogTitle>
              <div className="mt-4 flex flex-wrap gap-5 text-sm text-slate-300">
                <span className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#E0218A]" />{fmtDateTime(e.starts_at)}</span>
                {e.location && <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#E0218A]" />{e.location}</span>}
              </div>
              <DialogDescription className="mt-5 text-slate-300 leading-relaxed whitespace-pre-line">{e.description}</DialogDescription>
              <div className="mt-6 flex flex-wrap gap-3">
                {e.link && (
                  <a href={e.link} target="_blank" rel="noreferrer" className="btn-gold" data-testid="event-external-link">
                    Event link <ExternalLink className="h-4 w-4" />
                  </a>
                )}
                <Link to={`/companies/${e.company_id}`} className="btn-ghost" data-testid="event-host-profile-link">Host profile</Link>
              </div>
              <div className="mt-8">
                <p className="kicker mb-3">Partnership performance</p>
                {e.metrics ? (
                  <MetricGrid m={e.metrics} />
                ) : (
                  <div className="rounded-xl border border-dashed border-[#2A1240]/15 p-6 flex items-center gap-4" data-testid="metrics-locked">
                    <Lock className="h-5 w-5 text-[#E0218A]" />
                    <p className="text-sm text-slate-300">
                      Spend, ROI, deals and reach are visible to verified team members.{" "}
                      <Link to="/login" className="text-[#8E2BC4] underline underline-offset-4">Sign in</Link>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
